/**
 * Entry hover wireframe box (TrackballControl.cs:337-383 + WireframeBoxFeature / WireframeBoxRenderer.cs).
 *  - Which entry: the closest entry whose bounds the pointer ray hits (RaycastEntry, :304-333).
 *  - Shown only when that entry covers less than INTERACTION.entryHoverBoxCoverage (0.75) of the viewport: the 8
 *    bound corners projected to viewport space, clamped to [0, 1], area of their rectangle; any corner behind the
 *    camera hides it (EntryFitsOnScreen, :343-377).
 *  - Re-evaluated on pointer moves without buttons (:142-148), so it stays put during a drag.
 *  - Shape: Nanome draws corner brackets (EntryRendererWebGl.prefab _onlyCorners 1, _frameThickness 0.25): each
 *    corner pushed out by r2 = 0.25 * sqrt(6) / 3 along its diagonal, three half-edges of min(size) / 3, extents
 *    floored at 0.8 Å (WireframeBoxRenderer.cs:13, 395-458). Lite draws those as 1 px lines in the hover colour.
 *    WireframeBox.mat is white with an entry-name label on the three faces nearest the camera; neither the white
 *    material nor the labels are reproduced (see README "Known gaps").
 */
import { BufferAttribute, BufferGeometry, LineSegments, Matrix4, Vector3, type RawShaderMaterial } from 'three';
import { INTERACTION } from '@nanome-lite/look';
import { makeMaterial } from './gl';
import { RENDER_ORDER } from './impostors';
import { projectToScreen } from './camera';

/** WireframeBoxRenderer constants. */
export const ENTRY_BOX = {
  minHalfExtent: 0.8,
  frameThickness: 0.25,
  onlyCorners: true,
  coverage: INTERACTION.entryHoverBoxCoverage,
  color: 0xffffff, // WireframeBox.mat is white in Nanome (not the hover colour)
} as const;

/** Slab test: distance along the (unit or not) ray to the first hit with the box, or null. t >= 0 only. */
export function rayAabb(origin: Vector3, dir: Vector3, min: readonly number[], max: readonly number[]): number | null {
  let t0 = 0, t1 = Infinity;
  const o = [origin.x, origin.y, origin.z], d = [dir.x, dir.y, dir.z];
  for (let a = 0; a < 3; a++) {
    const lo = min[a]!, hi = max[a]!;
    if (Math.abs(d[a]!) < 1e-12) { if (o[a]! < lo || o[a]! > hi) return null; continue; }
    let ta = (lo - o[a]!) / d[a]!, tb = (hi - o[a]!) / d[a]!;
    if (ta > tb) [ta, tb] = [tb, ta];
    t0 = Math.max(t0, ta); t1 = Math.min(t1, tb);
    if (t0 > t1) return null;
  }
  return t0;
}

/**
 * Viewport fraction covered by the screen rectangle of the 8 world-space corners, each clamped to the viewport;
 * null when a corner is behind the camera (EntryFitsOnScreen returns false then).
 */
export function screenCoverage(cornersWorld: readonly Vector3[], projection: Matrix4): number | null {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const c of cornersWorld) {
    const s = projectToScreen(c, projection, 1, 1);
    if (!s) return null;
    const x = Math.min(1, Math.max(0, s.x)), y = Math.min(1, Math.max(0, s.y));
    minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y);
  }
  return (maxX - minX) * (maxY - minY);
}

/** Line segments (pairs of xyz) for the box of [min, max] in Å; corner brackets or all 12 edges. */
export function entryBoxSegments(min: readonly number[], max: readonly number[], onlyCorners: boolean = ENTRY_BOX.onlyCorners): Float32Array {
  const c = [0, 1, 2].map((a) => (min[a]! + max[a]!) / 2);
  const half = [0, 1, 2].map((a) => Math.max((max[a]! - min[a]!) / 2, ENTRY_BOX.minHalfExtent));
  const size = half.map((h) => 2 * h);
  const r2 = (ENTRY_BOX.frameThickness * Math.sqrt(6)) / 3;
  const taper = Math.min(size[0]!, size[1]!, size[2]!) / 3;
  const out: number[] = [];
  const corner = (sx: number, sy: number, sz: number): number[] => {
    const n = Math.hypot(1, 1, 1);
    return [c[0]! + sx * (half[0]! + r2 / n), c[1]! + sy * (half[1]! + r2 / n), c[2]! + sz * (half[2]! + r2 / n)];
  };
  for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const sz of [-1, 1]) {
    const p = corner(sx, sy, sz);
    for (let a = 0; a < 3; a++) {
      const s = [sx, sy, sz][a]!;
      if (onlyCorners) {
        const q = p.slice();
        q[a] = q[a]! - s * taper;
        out.push(...p, ...q);
      } else if (s < 0) {
        const q = p.slice();
        q[a] = q[a]! + 2 * (half[a]! + r2 / Math.hypot(1, 1, 1));
        out.push(...p, ...q);
      }
    }
  }
  return Float32Array.from(out);
}

const BOX_VERT = /* glsl */ `
precision highp float;
in vec3 position;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
void main() { gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
const BOX_FRAG = /* glsl */ `
precision highp float;
uniform vec3 uColor;
out vec4 fragColor;
void main() { fragColor = vec4(uColor * (uColor * (uColor * 0.305306011 + 0.682171111) + 0.012522878), 1.0); }
`;

/** The box drawable: one LineSegments under the scene root (domain Å), hidden until set. */
export class EntryHoverBox {
  readonly lines: LineSegments;
  private readonly material: RawShaderMaterial;
  entrySerial: number | null = null;

  constructor() {
    const col = ENTRY_BOX.color;
    this.material = makeMaterial(BOX_VERT, BOX_FRAG, null, {
      uniforms: { uColor: { value: new Vector3(((col >> 16) & 255) / 255, ((col >> 8) & 255) / 255, (col & 255) / 255) } }, blend: false,
    });
    this.lines = new LineSegments(new BufferGeometry(), this.material);
    this.lines.frustumCulled = false;
    this.lines.matrixAutoUpdate = false;
    this.lines.renderOrder = RENDER_ORDER.label + 1;
    this.lines.visible = false;
  }

  /** Show the box of an entry's Å bounds, or hide it (null). Returns true when something changed. */
  set(entrySerial: number | null, min?: readonly number[], max?: readonly number[]): boolean {
    if (entrySerial === this.entrySerial && (entrySerial === null || this.lines.visible)) return false;
    this.entrySerial = entrySerial;
    if (entrySerial === null || !min || !max) { this.lines.visible = false; return true; }
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(entryBoxSegments(min, max), 3));
    this.lines.geometry.dispose();
    this.lines.geometry = g;
    this.lines.visible = true;
    return true;
  }

  /** Force a rebuild next time (bounds changed). */
  invalidate(): void { this.entrySerial = null; this.lines.visible = false; }

  dispose(): void { this.lines.geometry.dispose(); this.material.dispose(); }
}
